import React, { useState, useEffect, useRef } from 'react';
import { providerAPI } from '../services/api';
import { formatProviderSiteOptionLabel } from '../utils/providerDisplayLabel';
import './SearchableProviderSelect.css';

const SEARCH_DEBOUNCE_MS = 250;

function siteKeyFromRow(row) {
  if (!row) return '';
  const id = row.payee_site_id ?? row.provider_site_id;
  return id != null && id !== '' ? String(id) : '';
}

/**
 * Searchable provider + office rows. Value = payee_site_id ?? provider_site_id, or legacy `provider:{id}`.
 * Parent supplies displayLabel for the current selection; optional typeCode limits to one provider type (e.g. DR).
 * onChange: { value, label, providerId, cleared? } — providerId empty when cleared or legacy provider-only value.
 * `cleared: true` when the user clicked ×; parents should treat only that as an intentional clear, not every empty payload.
 */
function SearchableProviderTeamOfficeSelect({
  value,
  displayLabel,
  onChange,
  placeholder = 'Click and type last name, first name, or type (e.g. DR)…',
  disabled,
  required,
  id,
  className,
  typeCode,
  formatOptionLabel = formatProviderSiteOptionLabel,
  hiddenInputName = 'provider_site_id',
  searchPlaceholder = 'Last name, first name, or type code…',
  resultHint = 'Type last name, first name, or type code — up to 25 offices shown.',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const searchSeqRef = useRef(0);
  const searchAbortRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (!query.trim()) {
      searchSeqRef.current += 1;
      searchAbortRef.current?.abort();
      setResults([]);
      setLoading(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchAbortRef.current?.abort();
      const ac = new AbortController();
      searchAbortRef.current = ac;
      searchSeqRef.current += 1;
      const seq = searchSeqRef.current;
      setLoading(true);
      providerAPI
        .searchSiteOptions(query.trim(), { limit: 500, signal: ac.signal, typeCode: typeCode || undefined })
        .then((res) => {
          if (seq !== searchSeqRef.current) return;
          setResults(res.data?.data ?? []);
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
          if (seq !== searchSeqRef.current) return;
          setResults([]);
        })
        .finally(() => {
          if (seq === searchSeqRef.current) setLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      searchAbortRef.current?.abort();
    };
  }, [query, isOpen, typeCode]);

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    setQuery('');
    setResults([]);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = (row) => {
    const key = siteKeyFromRow(row);
    if (!key || !String(key).trim()) return;
    const label = formatOptionLabel(row);
    onChange({
      value: key,
      label,
      providerId: row.provider_id != null ? String(row.provider_id) : '',
    });
    setIsOpen(false);
    setQuery('');
    setResults([]);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ value: '', label: '', providerId: '', cleared: true });
    setQuery('');
    setResults([]);
  };

  const labelTrim = (displayLabel || '').trim();
  const hasLabel = labelTrim.length > 0;
  const hasValue = value != null && String(value).trim() !== '';
  // Show saved label even if value prop is briefly empty (parent merge races); fallback to Office #id.
  const closedText = hasLabel
    ? labelTrim
    : hasValue
      ? `Office #${value}`
      : '';
  const showClear = (hasValue || hasLabel) && !disabled;

  return (
    <div
      ref={wrapperRef}
      id={id || undefined}
      className={`searchable-provider-select searchable-provider-team-office-select ${className || ''}`}
    >
      <input
        type="hidden"
        name={hiddenInputName}
        value={value || ''}
        required={required}
        readOnly
        tabIndex={-1}
        aria-hidden
      />
      <div
        className={`searchable-provider-select-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleOpen}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={id ? `${id}-listbox` : undefined}
        title={closedText || undefined}
      >
        <span className={!closedText ? 'placeholder' : ''}>
          {closedText || placeholder}
        </span>
        {showClear && (
          <button
            type="button"
            className="searchable-provider-select-clear"
            onClick={handleClear}
            aria-label="Clear selection"
          >
            ×
          </button>
        )}
        <span className="searchable-provider-select-arrow">▼</span>
      </div>
      {isOpen && (
        <div
          className="searchable-provider-select-dropdown"
          id={id ? `${id}-listbox` : undefined}
          role="listbox"
        >
          <input
            ref={inputRef}
            type="text"
            className="searchable-provider-select-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            autoComplete="off"
          />
          <div className="searchable-provider-select-results">
            {loading && <div className="searchable-provider-select-loading">Searching…</div>}
            {!loading && query.trim() && results.length === 0 && (
              <div className="searchable-provider-select-empty">No matches. Try a different search.</div>
            )}
            {!loading && !query.trim() && (
              <div className="searchable-provider-select-hint">{resultHint}</div>
            )}
            {!loading &&
              results.map((row) => {
                const oid = siteKeyFromRow(row);
                if (!oid || !String(oid).trim()) return null;
                const optKey = `${row.provider_id}-${oid}`;
                const label = formatOptionLabel(row);
                return (
                  <div
                    key={optKey}
                    className="searchable-provider-select-option"
                    onMouseDown={(e) => {
                      /* Keep focus in search input / avoid label+form stealing activation before click fires */
                      e.preventDefault();
                    }}
                    onClick={() => handleSelect(row)}
                    role="option"
                    title={label}
                  >
                    {label}
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchableProviderTeamOfficeSelect;
